// src/pages/Preferences.jsx
import { useUser } from '@clerk/clerk-react';
import { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext.jsx';
import { X, Moon, Sun, Monitor, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ClipLoader } from 'react-spinners';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

// Helper to get timezone offset dynamically (handles DST correctly)
const getOffset = tz => {
    try {
        const date = new Date();
        const fmt = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            timeZoneName: 'shortOffset',
        });
        return fmt.formatToParts(date).find(p => p.type === 'timeZoneName')?.value || '';
    } catch {
        return '';
    }
};

// Get all IANA timezones dynamically with computed offsets
const getTimezones = () => {
    try {
        return Intl.supportedValuesOf('timeZone').map(tz => ({
            value: tz,
            label: `${tz.replace(/_/g, ' ')} (${getOffset(tz)})`,
        }));
    } catch {
        // Fallback for older browsers
        return [
            { value: 'Africa/Nairobi', label: 'Africa/Nairobi (GMT+3)' },
            { value: 'UTC', label: 'UTC (GMT+0)' },
        ];
    }
};

// Get supported languages dynamically using Intl API
const getLanguages = () => {
    // Curated list of common languages with native display names
    const commonLanguageCodes = [
        'en',
        'sw',
        'fr',
        'es',
        'pt',
        'de',
        'it',
        'ar',
        'zh',
        'hi',
        'ja',
        'ko',
        'ru',
        'nl',
        'pl',
        'tr',
        'vi',
        'th',
        'id',
        'ms',
    ];

    try {
        const displayNames = new Intl.DisplayNames(['en'], { type: 'language' });
        return commonLanguageCodes
            .map(code => ({
                value: code,
                label: displayNames.of(code) || code,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    } catch {
        // Fallback
        return [
            { value: 'en', label: 'English' },
            { value: 'sw', label: 'Swahili' },
            { value: 'fr', label: 'French' },
            { value: 'es', label: 'Spanish' },
        ];
    }
};

export default function Preferences({ isOpen, onClose }) {
    const { user } = useUser();
    const { theme, setTheme } = useTheme();
    const [selectedTheme, setSelectedTheme] = useState(theme || 'system');
    const [selectedLanguage, setSelectedLanguage] = useState('en');
    const [selectedTimezone, setSelectedTimezone] = useState('Africa/Nairobi');
    const [isSaving, setIsSaving] = useState(false);

    // Memoize the timezone and language lists to avoid recomputing on every render
    const timezones = useMemo(() => getTimezones(), []);
    const languages = useMemo(() => getLanguages(), []);

    useEffect(() => {
        if (!isOpen) return;
        setSelectedTheme(theme || 'system');
        // Load saved preferences from user metadata
        const savedLanguage = user?.unsafeMetadata?.language || 'en';
        const savedTimezone = user?.unsafeMetadata?.timezone || null;

        // Detect browser timezone (IANA) and prefer it when no saved timezone exists
        let browserTz = null;
        try {
            const resolved = Intl.DateTimeFormat().resolvedOptions();
            browserTz = resolved?.timeZone || null;
        } catch {
            browserTz = null;
        }

        setSelectedLanguage(savedLanguage);
        setSelectedTimezone(savedTimezone || browserTz || 'Africa/Nairobi');
    }, [isOpen, theme, user]);

    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('modal-open');
            // Also directly style any aside elements
            const asides = document.querySelectorAll('aside');
            asides.forEach(aside => {
                aside.style.filter = 'blur(4px)';
                aside.style.pointerEvents = 'none';
            });
        } else {
            document.body.classList.remove('modal-open');
            const asides = document.querySelectorAll('aside');
            asides.forEach(aside => {
                aside.style.filter = '';
                aside.style.pointerEvents = '';
            });
        }
        return () => {
            document.body.classList.remove('modal-open');
            const asides = document.querySelectorAll('aside');
            asides.forEach(aside => {
                aside.style.filter = '';
                aside.style.pointerEvents = '';
            });
        };
    }, [isOpen]);

    const savePreferences = async () => {
        setIsSaving(true);
        try {
            await user.update({
                unsafeMetadata: {
                    theme: selectedTheme,
                    language: selectedLanguage,
                    timezone: selectedTimezone,
                },
            });

            // Persist + apply via ThemeContext (supports "system")
            setTheme(selectedTheme);
            toast.success('Preferences saved successfully.');
            onClose();
        } catch (error) {
            toast.error('Failed to save preferences.');
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    };

    const themeOptions = [
        { value: 'light', label: 'Light', icon: Sun },
        { value: 'dark', label: 'Dark', icon: Moon },
        { value: 'system', label: 'System', icon: Monitor },
    ];

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center z-999 px-4">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/40 backdrop-blur-xs z-999" onClick={onClose} />

            {/* Dialog */}
            <div className="relative bg-neutral-50 border border-neutral-300 rounded-xl shadow-2xl max-w-md w-full overflow-hidden z-999">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-5 border-neutral-300">
                    <h2 className="text-xl font-semibold text-neutral-950">Preferences</h2>
                    <button
                        onClick={onClose}
                        className="p-1.5 hover:bg-neutral-100 rounded-lg transition-colors text-neutral-700 hover:text-neutral-900"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="px-6 py-6 flex flex-col gap-6">
                    {/* Theme Selection */}
                    <div className="flex flex-col gap-3">
                        <label className="block text-sm font-medium text-neutral-700">Appearance</label>

                        {/* Theme Options as Cards */}
                        <div className="grid grid-cols-3 gap-3">
                            {themeOptions.map(({ value, label, icon: Icon }) => (
                                <button
                                    key={value}
                                    onClick={() => setSelectedTheme(value)}
                                    className={`
                    relative flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all
                    ${selectedTheme === value ? 'border-primary' : 'border-neutral-200 hover:border-accent hover:text-accent'}
                  `}
                                >
                                    {selectedTheme === value && (
                                        <div className="absolute top-2 right-2 w-4 h-4 rounded-full flex items-center justify-center">
                                            <Check className="w-3 h-3 text-neutral-900" />
                                        </div>
                                    )}
                                    <Icon
                                        className={`w-5 h-5 ${selectedTheme === value ? 'text-primary' : 'text-neutral-700'}`}
                                    />
                                    <span
                                        className={`text-sm font-medium ${
                                            selectedTheme === value ? 'text-primary' : 'text-neutral-700'
                                        }`}
                                    >
                                        {label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Language Selection */}
                    <div className="hidden flex-col gap-2">
                        <label className="block text-sm font-medium text-neutral-700">Language</label>
                        <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select a language" />
                            </SelectTrigger>
                            <SelectContent>
                                {languages.map(({ value, label }) => (
                                    <SelectItem key={value} value={value}>
                                        {label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Timezone Selection */}
                    <div className="flex flex-col gap-2">
                        <label className="block text-sm font-medium text-neutral-700">Timezone</label>
                        <Select value={selectedTimezone} onValueChange={setSelectedTimezone}>
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select a timezone" />
                            </SelectTrigger>
                            <SelectContent>
                                {timezones.map(({ value, label }) => (
                                    <SelectItem key={value} value={value}>
                                        {label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-3 px-6 py-4 bg-neutral-50 border-neutral-300">
                    <Button variant="outline" onClick={onClose} disabled={isSaving} className="">
                        Cancel
                    </Button>
                    <Button onClick={savePreferences} disabled={isSaving} className="text-neutral-950">
                        {isSaving ? (
                            <span className="flex items-center gap-2">
                                <ClipLoader size={16} color="#fff" />
                            </span>
                        ) : (
                            'Save Changes'
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}
